import { Bar, BarChart, Text as RechartsText, ResponsiveContainer, XAxis, YAxis } from 'recharts';

import { Box, Spinner, Text } from '@zillow/constellation-10';

import { PropertiesSelectors, RoutingSelectors } from '../../../../selectors';
import { useSelector } from '../../../../selectors/useSelector';

import { useFetchCompStats } from '../../../../hooks/queries/graphql';
import numberUtils from '../../../../utils/numberUtils';
import type { LISTING_SUMMARY_DISPLAY_STATUS } from '../../../../constants/ListingSummaryConstants';

import { StyledBarChartContainer, StyledStatisticsComponent } from './StyledComponents';
import { flexStyles } from '../../styles';
import CompsCTA from '../CompsCTA';

// offset for the chart's top margin in order to get the label above the bars to display
const CHART_CONFIG = {
  OFFSET_TOP: 20,
  BAR_GAP: 8,
  BAR_SIZE: 16,
};

// bar layout starts to get funky if less than these values
// real height of the chart is set via css: StyledBarChartContainer
const CHART_HEIGHT = {
  BASE: 100,
};

const CHART_LABEL = {
  INQUIRIES: 'Inquiries',
  VIEWS: 'Views',
};

const BAR_NAME = {
  MY_LISTINGS: 'Your Listing',
  COMPARABLES: 'Comparables',
};

const BAR_FILL = {
  PURPLE: '#5b33ad',
  LIGHT_BLUE: '#2bc1e1',
};

type Props = {
  hasAnalyticsRentComps?: boolean;
  status: keyof typeof LISTING_SUMMARY_DISPLAY_STATUS;
};

const Statistics = ({ hasAnalyticsRentComps = false, status }: Props) => {
  const listingAlias = useSelector((state) => RoutingSelectors.makeListingId(state));
  const { data, isLoading, error } = useFetchCompStats(listingAlias);
  const { city, state, street, unit, zip } = useSelector((reduxState) =>
    PropertiesSelectors.makeCurrentProperty(reduxState),
  );

  if (isLoading) {
    return (
      <Box css={{ display: 'flex', justifyContent: 'center', alignContent: 'center' }}>
        <Spinner size="sm" />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box css={{ overflow: 'auto', ...flexStyles }}>
        <Text textStyle="body-sm">{error?.message || 'There was an error fetching listing statistics.'}</Text>
      </Box>
    );
  }

  const { stats, similarFloorplans } = data;

  // listing
  const {
    startDate: myListingStartDate,
    endDate: myListingEndDate,
    views: myListingViews,
    inquiries: myListingInquiries,
  } = stats;
  const { total: myListingViewsTotal } = myListingViews;
  const { total: myListingInquiriesTotal } = myListingInquiries;

  // comps
  const { stats: compStats } = similarFloorplans;
  const { views: compViews, inquiries: compInquiries } = compStats;

  // only need to use one set of start and end dates
  // myListingStartDate and compStartDate should be the same
  const title = `Activity for ${myListingStartDate}-${myListingEndDate}`;

  const { total: compViewsTotal } = compViews;
  const { total: compInquiriesTotal } = compInquiries;

  const viewsData = [
    {
      name: CHART_LABEL.VIEWS,
      [BAR_NAME.MY_LISTINGS]: myListingViewsTotal,
      [BAR_NAME.COMPARABLES]: compViewsTotal,
    },
  ];

  const inquiriesData = [
    {
      name: CHART_LABEL.INQUIRIES,
      [BAR_NAME.MY_LISTINGS]: myListingInquiriesTotal,
      [BAR_NAME.COMPARABLES]: compInquiriesTotal,
    },
  ];

  const address = {
    city,
    state,
    street,
    unit,
    zip,
  };

  // @ts-expect-error
  const returnCustomBarLabel = (rechartProps) => {
    const { fill, label, value, viewBox, y } = rechartProps;

    const { width } = viewBox;
    const calcX = parseInt(width, 10) + 10;

    const response = `${numberUtils.formatWithK(value)} • ${label}`;

    return (
      <RechartsText
        x={calcX}
        y={y}
        dy={12}
        fontSize="12"
        fontFamily="OpenSans, sans-serif"
        fill={fill}
        //  @ts-expect-error
        position="right"
      >
        {response}
      </RechartsText>
    );
  };

  //  @ts-expect-error
  const renderCustomXAxisLabel = (rechartProps) => {
    const { label } = rechartProps;

    return (
      <>
        {/* @ts-expect-error */}
        <RechartsText x={0} y={15} dy={0} fontSize="16" fontFamily="sans-serif" position="right">
          {label}
        </RechartsText>
      </>
    );
  };

  //  @ts-expect-error
  const getBuffer = (bufferData) => {
    const obj = bufferData[0];
    const maxVal = Math.max(obj[BAR_NAME.MY_LISTINGS], obj[BAR_NAME.COMPARABLES]).toString().split('');

    let bufferString = '5';
    maxVal.forEach(() => {
      bufferString += '0';
    });

    // remove last digit
    bufferString = bufferString.slice(0, -1);

    return `dataMax + ${bufferString}`;
  };

  //  @ts-expect-error
  const renderBarChart = (barChartData, label) => {
    // validation
    if (!barChartData) {
      console.error('barChartData is required', barChartData);
      return false;
    }

    if (!label) {
      console.error('label is required', label);
      return false;
    }

    // set false to fix label sometimes not displaying on load
    // ref: https://github.com/recharts/recharts/issues/1135
    const isAnimationActive = false;

    return (
      <ResponsiveContainer
        className={`ResponsiveContainer-BarChart ResponsiveContainer-BarChart-${label}`}
        width="100%"
        height={CHART_HEIGHT.BASE}
        debounce={0}
      >
        <BarChart
          data={barChartData}
          layout="vertical"
          margin={{
            top: CHART_CONFIG.OFFSET_TOP,
            right: 0,
            left: 0,
            bottom: 0,
          }}
          barGap={CHART_CONFIG.BAR_GAP}
          barSize={CHART_CONFIG.BAR_SIZE}
          stackOffset="sign"
        >
          <XAxis
            type="number"
            hide={false}
            axisLine={false}
            tick={false}
            domain={[0, getBuffer(barChartData)]}
            label={{ content: renderCustomXAxisLabel, label }}
          />

          <YAxis type="category" dataKey="name" hide={true} />

          <Bar
            dataKey={BAR_NAME.MY_LISTINGS}
            fill={BAR_FILL.PURPLE}
            minPointSize={1}
            //  @ts-expect-error
            label={{ content: returnCustomBarLabel, label: BAR_NAME.MY_LISTINGS }}
            isAnimationActive={isAnimationActive}
          />

          <Bar
            dataKey={BAR_NAME.COMPARABLES}
            fill={BAR_FILL.LIGHT_BLUE}
            minPointSize={1}
            //  @ts-expect-error
            label={{ content: returnCustomBarLabel, label: BAR_NAME.COMPARABLES }}
            isAnimationActive={isAnimationActive}
          />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  return (
    <StyledStatisticsComponent className="Statistics">
      <Text asChild textStyle="body-sm-bold" css={{ marginBottom: '200' }}>
        <p>{title}</p>
      </Text>
      <StyledBarChartContainer className="BarChart-Container">
        {renderBarChart(viewsData, CHART_LABEL.VIEWS)}
        {renderBarChart(inquiriesData, CHART_LABEL.INQUIRIES)}
      </StyledBarChartContainer>

      <CompsCTA address={address} status={status} hasAnalyticsRentComps={hasAnalyticsRentComps} />
    </StyledStatisticsComponent>
  );
};

export default Statistics;
